import * as React from 'react';
import clsx from 'clsx';
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import Drawer, { DrawerProps } from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { Omit } from '@material-ui/types';
import { OperationContext } from './OperationContext';
import { NavigatorData } from './NavigatorData';
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from './AuthContext';
import { Button, Box, Grid } from '@material-ui/core';
import { Themes } from '../styling/Themes';
import { DeltaXMLColors, DeltaJSONColors } from '../styling/DeltaColors';
import { CustomerSubscription, CustomerSubscriptionStatus } from './SubscriptionData';
import { SubscriptionRequestStatus, SubscriptionRequestData } from './Paperbase';

export interface NavigatorEntry {
  id: string,
  icon: JSX.Element;
  active?: boolean;
  link: string;
}

export interface NavigatorSection {
  id: string,
  children: NavigatorEntry[]
}

export interface UnauthorizedNavigatorEntry {
  id: string,
  icon: JSX.Element;
  active?: boolean;
  link: string;
}

export interface UnauthorizedNavigatorSection {
  id: string,
  children: UnauthorizedNavigatorEntry[]
}

const styles = (theme: Theme) =>
  createStyles({
    categoryHeader: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    item: {
      paddingTop: 1,
      paddingBottom: 1,
      color: DeltaJSONColors.WHITE,
      '&:hover': {
        backgroundColor: DeltaXMLColors.OXFORD_BLUE,
        color: DeltaXMLColors.CELERY,
      },
      '&:focus': {
        backgroundColor: DeltaXMLColors.OXFORD_BLUE,
        color: DeltaXMLColors.CELERY,
      }
    },
    logoItem: {
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND,
      justifyContent: 'center',
    },
    itemActiveItem: {
      color: DeltaXMLColors.CELERY,
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    itemIcon: {
      minWidth: 'auto',
      marginRight: theme.spacing(2),
    },
    divider: {
      marginTop: theme.spacing(2),
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    topDivider: {
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    appLogo: {
      height: Themes.appLogoDimensions.height,
      width: Themes.appLogoDimensions.width
    },
    buttonGrid: {
      justifyContent: 'center',
      display: 'flex',
      margin: '10px'
    }
  });

export interface NavigatorProps
  extends Omit<DrawerProps, 'classes'>,
  WithStyles<typeof styles> {
  title: string,
  sections: NavigatorSection[],
  ishome: boolean,
  unauthorizedSections: UnauthorizedNavigatorSection[],
  customerSubscriptionData: CustomerSubscription,
  requestStatus: SubscriptionRequestData
}

function isActiveLink(pathPart1: string, href: string) {
  const hrefPart = href.substring(1);
  return pathPart1 === hrefPart;
}

function Navigator(props: NavigatorProps) {
  const opContext = React.useContext(OperationContext);
  const { classes, ishome, customerSubscriptionData, requestStatus, ...other } = props;
  const windowPath = useLocation();
  const pathPart1 = windowPath.pathname.split('/')[1];
  const auth = React.useContext(AuthContext);
  const section = (auth.isAuthenticated() && customerSubscriptionData.subscriptionStatus !== CustomerSubscriptionStatus.NULL) && requestStatus.subscriptions.status !== SubscriptionRequestStatus.errorResponse ? 
                    props.sections : props.unauthorizedSections;

  const getRealLinkRef = (refFromNavData: string) => {
    let newRef = '';
    switch (refFromNavData) {
      case NavigatorData.configBaseUrl:
        newRef = NavigatorData.getLinkRefForOperation(NavigatorData.configBaseUrl, opContext.operationType);
        break;
      // case NavigatorData.sessionJobsBaseUrl:
      //   newRef = NavigatorData.getLinkRefForOperation(NavigatorData.sessionJobsBaseUrl, opContext.operationType);
      //   break;
      case NavigatorData.jobsHistoryBaseUrl:
        newRef = NavigatorData.getLinkRefForOperation(NavigatorData.jobsHistoryBaseUrl, opContext.operationType);
        break;
      default:
        newRef = refFromNavData;
    }
    return newRef;
  }
  
  return (
    <Drawer variant="permanent" {...other}>
      <List disablePadding>
        <ListItem className={clsx(classes.logoItem)}>
          <img src="/DeltaJSON_Logo_White.svg" alt="DeltaJSON" className={classes.appLogo}/>
        </ListItem>
        <Divider className={classes.topDivider}/>
        {section.map(({ id, children }) => (
          <React.Fragment key={id}>
            <ListItem className={classes.categoryHeader}>
            </ListItem>
            {children.map(({ id: childId, link: linkRef, icon, active }) => (
              <NavLink 
              key={childId} 
              to={getRealLinkRef(linkRef)} 
              className={clsx(classes.item, isActiveLink(pathPart1, linkRef) && classes.itemActiveItem)} style={{ textDecoration: 'none' }}>
                <ListItem
                  key={childId}
                  button
                  className={clsx(classes.item, isActiveLink(pathPart1, linkRef) && classes.itemActiveItem)}
                >
                  <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
                  <ListItemText>
                    {childId}
                  </ListItemText>

                </ListItem>
              </NavLink>
            ))}
            <Divider className={classes.divider} />
          </React.Fragment>
        ))}
        { (auth.isAuthenticated()) ? <Box justifyContent="center" display="flex" my="1rem">
          <Grid container>
            <Grid item xs={12} className={classes.buttonGrid}>
              <Button variant="contained" color="secondary" id="feedbackButton">Submit Feedback</Button>
            </Grid>
            <Grid item xs={12} className={classes.buttonGrid}>
              <Button variant="contained" color="secondary" onClick={() => window.open('https://www.deltaxml.com/contact/', '_blank')}>Contact Us</Button>
            </Grid>
          </Grid>
        </Box> : <></>}
        
      </List>
    </Drawer>
  );
}

export default withStyles(styles)(Navigator);
